import { observer } from 'mobx-react-lite';
import { WorkspacePanel } from '../panels/workspace-panel';
import React from 'react';
import { WorkspacePanelProvider } from '../panels/workspace-panel-provider';
import { Panel } from '../panels/panel';
import {
    ExandablePanelSizeProvider,
    useExpandablePanelSizeState,
} from '../panels/expandable-panel-size';
import { VStack, Box, Text } from '@cad-arc';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { useOpenBoloPanel } from '../bolos/bolo-panel/use-open-bolo-panel';
import { AttachmentsManagerRegister } from './attachments-manager-register';
import { AttachmentsPreview } from './attachments-preview';
import { AttachmentsPanelType } from './types';
import { useAttachmentEntityNumber } from './hooks/use-attachment-entity-number';

const AttachmentsPanelInner = observer(function AttachmentsPanelInner({
    panel,
}: {
    panel: WorkspacePanel;
}) {
    const { resizeOptions, sizeProps } = useExpandablePanelSizeState(panel, 'MEDIA_ATTACHMENTS');
    const mediaPanel = panel as WorkspacePanel<'MEDIA_ATTACHMENTS'>;
    const openEventPanel = useOpenEventPanel();
    const openBoloPanel = useOpenBoloPanel();
    const [selectedAttachmentName, setSelectedAttachmentName] =
        React.useState<string>('Attachments Preview');

    const attachmentId = mediaPanel.getPanelState('attachmentId');
    const sortOption = mediaPanel.getPanelState('sortOption');
    const isReadOnly = mediaPanel.getPanelState('isReadOnly');
    const entityNumberFromPanelState = mediaPanel.getPanelState('entityNumber');
    const entityNumberState = useAttachmentEntityNumber({
        entityId: panel.linkedEntityId,
        entityType: panel.linkedEntityType as AttachmentsPanelType | undefined,
        entityNumberFromPanelState,
    });

    const handleOpenEventPanel = () => {
        if (panel.linkedEntityId) {
            openEventPanel({
                eventId: panel.linkedEntityId,
                shouldFocusPanel: true,
            });
        }
    };

    const handleOpenBoloPanel = () => {
        if (panel.linkedEntityId) {
            openBoloPanel({
                boloId: panel.linkedEntityId,
                shouldFocus: true,
            });
        }
    };
    return (
        <>
            <AttachmentsManagerRegister />
            <Panel
                panel={panel}
                body={
                    <Box overflowY="auto" height="100%">
                        <VStack spacing={4} align="start" height="100%">
                            {panel.linkedEntityId &&
                                panel.linkedEntityType &&
                                (entityNumberState.status === 'loading' ? (
                                    <LoadingIndicator />
                                ) : (
                                    <AttachmentsPreview
                                        entityId={panel.linkedEntityId?.toString()}
                                        entityType={panel.linkedEntityType as AttachmentsPanelType}
                                        entityNumber={entityNumberState.value}
                                        attachmentId={attachmentId}
                                        sortOption={sortOption}
                                        onSelectedAttachmentChange={setSelectedAttachmentName}
                                        onOpenEventPanel={
                                            panel.linkedEntityType === 'CAD_AGENCY_EVENT'
                                                ? handleOpenEventPanel
                                                : undefined
                                        }
                                        onOpenBoloPanel={
                                            panel.linkedEntityType === 'BOLO'
                                                ? handleOpenBoloPanel
                                                : undefined
                                        }
                                        isReadOnly={isReadOnly}
                                    />
                                ))}
                        </VStack>
                    </Box>
                }
                header={
                    <Box p={2}>
                        <Text fontWeight="bold">{selectedAttachmentName}</Text>
                    </Box>
                }
                resizeOptions={resizeOptions}
                {...sizeProps}
                closeButtonProps={{ size: 'sm' }}
            />
        </>
    );
});

export const AttachmentsPanel = observer(function AttachmentsPanel({
    panel,
}: {
    panel: WorkspacePanel;
}): React.ReactElement {
    return (
        <WorkspacePanelProvider workspacePanel={panel}>
            <ExandablePanelSizeProvider>
                <AttachmentsPanelInner panel={panel} />
            </ExandablePanelSizeProvider>
        </WorkspacePanelProvider>
    );
});
